import { useEffect, useRef, useState } from "react";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { setLeftTopInStyle } from "../modelViews/GraficElementUtils";
import BaseObject from "../models/BaseObject";
import Point from "../models/Point";
import { runUiCloseAnimation } from "./GuideUtils";
import { itx } from "../translationItx/itx";

var mouseIsDown = false;
var pStart = { x: 0, y: 0 };
var saveTransition: string = "";

var lastPos = new Point(-1, -1);

export default function GuideUi() {
  useOnChangeWatcher(state.guideMode.updateUiTrigger, (changedProperty: any, changedObject: BaseObject) => {
    if (!state.guideMode.sequenceRunning) {
      setFocusToOkButton();
    }
    return true;
  });

  const refButtonOk = useRef<HTMLButtonElement>(null);
  const refButtonWeiter = useRef<HTMLButtonElement>(null);

  const moveRef = useRef<HTMLDivElement>(null);

  const [geheZurSeite, setGeheZurSeite] = useState(false);
  const pendingTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const adjustLineTimer = useRef<ReturnType<typeof setTimeout>>();
  const focusOkTimer = useRef<ReturnType<typeof setTimeout>>();
  const focusWeiterTimer = useRef<ReturnType<typeof setTimeout>>();

  function scheduleTimeout(callback: () => void, delay: number) {
    let timerId: ReturnType<typeof setTimeout>;
    timerId = setTimeout(() => {
      pendingTimers.current.delete(timerId);
      callback();
    }, delay);
    pendingTimers.current.add(timerId);
    return timerId;
  }

  function cancelScheduledTimeout(timerId: ReturnType<typeof setTimeout> | undefined) {
    if (timerId !== undefined) {
      clearTimeout(timerId);
      pendingTimers.current.delete(timerId);
    }
  }

  useEffect(() => {
    return () => {
      pendingTimers.current.forEach((timerId) => clearTimeout(timerId));
      pendingTimers.current.clear();
    };
  }, []);

  function setFocusToWeiterButton() {
    cancelScheduledTimeout(focusWeiterTimer.current);
    focusWeiterTimer.current = scheduleTimeout(() => {
      focusWeiterTimer.current = undefined;
      if (refButtonWeiter.current) {
        refButtonWeiter.current.focus();
      }
    }, 100);
  }

  function setFocusToOkButton() {
    cancelScheduledTimeout(focusOkTimer.current);
    focusOkTimer.current = scheduleTimeout(() => {
      focusOkTimer.current = undefined;
      if (refButtonOk.current) {
        refButtonOk.current.focus();
      }
    }, 200);
  }

  useEffect(() => {
    setFocusToOkButton();
  }, []);

  //
  // Make sure GuideUi is inside ScreenLimits
  //

  function adjustUiPosition() {
    cancelScheduledTimeout(adjustLineTimer.current);
    if (moveRef.current) {
      const r = moveRef.current.children[0].getBoundingClientRect();
      if (state.guideMode.sequenceRunning) {
        if (state.guideMode.currentStep?.dontSlideAway) {
          return;
        }
        if (state.guideMode.explain) {
          //
          // Mitte unten
          //
          lastPos = new Point(window.innerWidth / 2, window.innerHeight * 0.8);
        } else {
          //
          // Rechts unten
          //
          lastPos = new Point(window.innerWidth - r.width - 20, window.innerHeight - r.height - 20);
        }
      } else {
        //
        // Mittig
        //
        lastPos = new Point(window.innerWidth / 2, window.innerHeight * 0.4);
      }

      moveRef.current.style.left = `${lastPos.x}px`;
      moveRef.current.style.top = `${lastPos.y}px`;
      adjustLineTimer.current = scheduleTimeout(() => {
        adjustLineTimer.current = undefined;
        adjustLineToExplains();
      }, 1000);
    }
  }

  useEffect(() => {
    adjustUiPosition();

    const guideUi_Inner = document.getElementById("guideUi_Inner");
    if (guideUi_Inner) {
      const timerId = scheduleTimeout(() => {
        guideUi_Inner.style.transition = "left 1s, top 1s"; // no itx
      }, 100);
      return () => cancelScheduledTimeout(timerId);
    }
  });

  //////////////////////////////////////////////////////////////////////////////////////

  //
  // moveHandling for moving GuideUi
  //

  function onMouseMove(event: any) {
    const elm = moveRef.current;
    if (elm) {
      if (!mouseIsDown) {
        return;
      }

      event.stopPropagation();
      event.preventDefault();

      const p = {
        x: event.clientX || event.touches[0]?.clientX,
        y: event.clientY || event.touches[0]?.clientY,
      };

      const rect = elm.getBoundingClientRect();
      const pCurrent = new Point(rect.left, rect.top);

      const delta = { x: p.x - pStart.x, y: p.y - pStart.y };

      pCurrent.x += delta.x;
      pCurrent.y += delta.y;

      lastPos = pCurrent;

      setLeftTopInStyle(elm, pCurrent);
      pStart = p;
      adjustLineToExplains();
    }
  }

  function onMouseDown(event: any) {
    const elm = moveRef.current;
    if (elm) {
      mouseIsDown = true;
      saveTransition = elm.style.transition;
      elm.style.transition = "";
      pStart = {
        x: event.clientX || event.touches[0]?.clientX,
        y: event.clientY || event.touches[0]?.clientY,
      };
    }
  }
  function onMouseUp(event: any) {
    const elm = moveRef.current;
    if (elm) {
      mouseIsDown = false;
      elm.style.transition = saveTransition;
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////

  function adjustLineToExplains() {
    const lineElm = document.getElementById("LineToExplainObject");

    const explainElmement = state.guideMode.explain?.elm;
    const avatarElm = document.querySelector(".avatar") as HTMLElement; // no itx

    const posExplainElm = explainElmement && state.guideMode.guide?.getCenterPoint(explainElmement);
    const posAvatarElm = state.guideMode.guide?.getCenterPoint(avatarElm);

    if (lineElm && avatarElm && explainElmement && posExplainElm && posAvatarElm) {
      const html = `<line
      x1=${posAvatarElm.x}px
      y1=${posAvatarElm.y}px
      x2=${posExplainElm.x}px
      y2=${posExplainElm.y}px
      stroke=var(--guide-background-explain)
      stroke-width=5
      vector-effect=non-scaling-stroke
      stroke-linecap=round
    />`;
      lineElm.innerHTML = html;
    }
  }
  function LineToExplainObject() {
    let opacityTimerId: ReturnType<typeof setTimeout> | undefined;
    useEffect(() => {
      const timerId = scheduleTimeout(() => {
        setFocusToWeiterButton();

        adjustLineToExplains();

        opacityTimerId = scheduleTimeout(() => {
          const lineElm = document.getElementById("LineToExplainObject");
          if (lineElm) {
            lineElm.style.opacity = "1";
          }
        }, 100);
      }, 1100);

      return () => {
        cancelScheduledTimeout(timerId);
        cancelScheduledTimeout(opacityTimerId);
      };
    }, []);

    const explainElmement = state.guideMode.explain?.elm;

    if (explainElmement) {
      const posExplainElm = state.guideMode.guide?.getCenterPoint(explainElmement);

      const avatarElm = document.querySelector(".avatar") as HTMLElement; // no itx
      const posAvatarElm = state.guideMode.guide?.getCenterPoint(avatarElm);

      if (posExplainElm && posAvatarElm) {
        return (
          <svg
            id="LineToExplainObject"
            style={{ opacity: "0", transition: "opacity 1s" }}
            preserveAspectRatio="none"
          ></svg>
        );
      }
    }

    return null;
  }

  function calcButtons(): any {
    const buttons: any = [];

    if (!geheZurSeite || state.guideMode.explain) {
      buttons.push(
        <div
          tabIndex={2}
          style={{
            cursor: "pointer",
            height: "var(--input-height)",
            margin: "5px",
            display: "inline-block",
            fontWeight: "400",
            color: "#212529",
            textAlign: "center",
          }}
          onClick={async (ev) => {
            if (state.guideMode.guide) {
              state.guideMode.guide.cancel = true;
            }
            state.guideMode.currentStorylineIndex = 0;
            state.guideMode.isHidden = true;

            if (state.guideMode.explain) {
              state.guideMode.explain.done();
            } else {
              runUiCloseAnimation();
            }
          }}
        >
          <div className="close">{itx("close", "Schließen")}</div>
        </div>,
      );
    }

    if (
      // state.guideMode.currentMode.value != GuideState.howCanIhelpYou &&
      // state.guideMode.toDoItem == undefined &&
      !state.guideMode.sequenceRunning
    ) {
      buttons.push(
        <button
          tabIndex={1}
          ref={refButtonOk}
          className="button"
          onClick={async (ev) => {
            if (!state.guideMode.currentStep?.dontSlideAway) {
              setGeheZurSeite(true);
            }
            let t = 100;

            //
            // Update position of roboter on screen
            //
            scheduleTimeout(async () => {
              if (state.guideMode.currentStep) {
                scheduleTimeout(() => {
                  adjustUiPosition();
                }, 10);

                //
                // execute this step
                //
                await state.guideMode.runSequence(ev, state.guideMode.currentStep);

                setGeheZurSeite(false);

                if (state.guideMode.isHidden) {
                  //
                  // if this step closed the guide, hidde to menu
                  //
                  runUiCloseAnimation();
                } else {
                  //
                  // go foreward to next step
                  //
                  // the next step will show the ui again,
                  // to go the the rest of the sequence steps
                  //
                  state.guideMode.forwardDoneSteps();
                }
              }
            }, t);
          }}
        >
          {itx("ok", "Okay")}
        </button>,
      );
    }

    if (state.guideMode.explain) {
      buttons.push(
        <button
          tabIndex={1}
          ref={refButtonWeiter}
          className="button"
          onClick={async (ev) => {
            if (state.guideMode.explain) {
              state.guideMode.explain.done();
            }
          }}
        >
          {itx("next", "Weiter")}
        </button>,
      );
    }

    return buttons;
  }
  /////////////////////////////////////////////////////////////////////////////////////////////////

  //
  // render
  //

  let style1: any = {
    zIndex: "1000",
    position: "absolute",
    left: "0px",
    top: "0px",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",

    // pointerEvents: "auto" // disable any click in App
    pointerEvents: "none", // enable clicks in App
  };

  if (state.guideMode.isHidden) {
    return null;
  }

  let text: any = state.guideMode.currentText;

  if (state.guideMode.explain) {
    text = state.guideMode.explain.text;
  } else if (geheZurSeite) {
    text = <div style={{ fontSize: "0.7em" }}>{itx("guide.watchBriefly", "Schau kurz zu")}</div>;
  }

  let textElement: any = [];
  if (typeof text == "string") {
    text.split("\n").forEach((line) => {
      if (line) {
        textElement.push(<div>{line || " "}</div>);
      } else {
        textElement.push(<br />);
        // textElement.push(<br />);
      }
    });
  } else {
    textElement = text;
  }

  let buttons = calcButtons();

  let className = "content"; // no itx
  if (state.guideMode.explain) {
    className += " explain"; // no itx
  }

  let selectionElements: any = [];
  const interactiveSelection = state.guideMode.currentStep?.interactiveSelection;
  if (interactiveSelection && !state.guideMode.explain) {
    buttons = null;
    const elements = new Array<any>();

    interactiveSelection()?.forEach((selectionOption) => {
      elements.push(
        <button
          className="button"
          onClick={async (ev) => {
            selectionOption.execute();
          }}
        >
          {selectionOption.text}
        </button>,
      );
    });
    elements.push(
      <div
        tabIndex={2}
        style={{
          cursor: "pointer",
          height: "var(--input-height)",
          margin: "5px",
          display: "inline-block",
          fontWeight: "400",
          color: "#212529",
          textAlign: "center",
          width: undefined,
          alignContent: "center",
          alignSelf: "center",
        }}
        onClick={async (ev) => {
          if (state.guideMode.guide) {
            state.guideMode.guide.cancel = true;
          }
          state.guideMode.currentStorylineIndex = 0;

          if (state.guideMode.explain) {
            state.guideMode.explain.done();
          } else {
            runUiCloseAnimation();
          }
        }}
      >
        <div className="close" style={{ width: "max-content" }}>
          {itx("close", "Schließen")}
        </div>
      </div>,
    );

    // because of close
    selectionElements = <div style={{ display: "flex", flexDirection: "column" }}>{elements}</div>;
    if (elements.length <= 1) {
      const text = itx("ToDoItemLine.allStorylinesDone").split("\n\n");

      textElement = new Array<any>();
      textElement.push(
        <div>
          {text[0]} <br />
          <br />
        </div>,
      );
      textElement.push(
        <div>
          {text[2]}
          <br />
          <br />
        </div>,
      );
    }
  } else {
    buttons = calcButtons();
  }

  let content: any = (
    <div
      className={className}
      style={{
        position: "absolute",
        width: "max-content",
        top: "0px",
        left: "0px",
        transform: geheZurSeite
          ? "translate(-50%, 37px)" // no itx
          : "translate(-50%, 75px)", // no itx
        transition: "transform 1s",
      }}
    >
      {textElement}
      {selectionElements}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          marginTop: "10px",
          justifyContent: "space-evenly",
        }}
      >
        {(!geheZurSeite || state.guideMode.explain) && buttons}
      </div>
    </div>
  );

  return (
    <div className="Theme guideUi" style={style1}>
      {state.guideMode.explain && <LineToExplainObject />}

      <div
        id="guideUi_Inner"
        ref={moveRef}
        style={{
          position: "absolute",
          left: lastPos.x,
          top: lastPos.y,
          pointerEvents: "auto",
        }}
      >
        <div
          className="avatar"
          style={{
            position: "absolute",
            cursor: "move",

            width: "150px",
            height: "150px",

            transform: geheZurSeite
              ? "translate(-50%, -50%) scale(0.5) " // no itx
              : "scale(1) translate(-50%, -50%)", // no itx

            transitionDelay: "2s",
            transition: "width 1s, height 1s, opacity 2s, transform 1s",
            pointerEvents: "auto",
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          <img src="/images/ESTADA Robot.jpg" style={{ pointerEvents: "none", width: "100%", height: "100%" }} />{" "}
        </div>

        <div
          style={{
            position: "absolute",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",

            width: "max-content",
            height: "max-content",

            pointerEvents: "none",
          }}
        >
          {content}
        </div>
      </div>
    </div>
  );
}
